import React, { useEffect, useState } from "react";
import {
  serverGetCategory,
  serverGetProductBaseMetalData,
  serverGetProductPlatingData,
  serverGetProductStoneTypeData,
  serverGetProductTrendData,
  serverGetProductBrandData,
  serverGetProductColorData,
  serverGetProductOccasionData,
  serverGetProductTypeData,
  serverInsertProduct,
  serverUpdateProduct,
} from "../../../services/serverApi";
import * as Yup from "yup";
import { ICategory } from "../../../types/category";
import { IProduct, ProductDivision } from "../../../types/product.d";

interface ProductInformationData {
  productBaseMetalData: any[];
  productPlatingData: any[];
  productStoneTypeData: any[];
  productTrendData: any[];
  productBrandData: any[];
  productColorData: any[];
  productOccasionData: any[];
  productTypeData: any[];
}

const useAddProduct = (
  handleClose: () => void,
  getProductData: () => void,
  editDataObject: IProduct | null,
  open: boolean
) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [categoryData, setCategoryData] = useState<ICategory[]>([]);
  const [division, setDivision] = useState<number>(ProductDivision?.Division1);
  const [selectedImage, setSelectedImage] = useState<{
    image1: string | File;
    image2: string | File;
    image3: string | File;
    image4: string | File;
  }>({
    image1: editDataObject?.image[0]?.path || '', 
    image2: editDataObject?.image[1]?.path || '', 
    image3: editDataObject?.image[2]?.path || '',
    image4: editDataObject?.image[3]?.path || ''
  });
  const [selectedVideo, setSelectedVideo] = useState<string | File | undefined>(
    editDataObject?.videoPath || undefined
  );
  const [productInformationData, setProductInformationData] = useState<ProductInformationData>({
    productBaseMetalData: [],
    productPlatingData: [],
    productStoneTypeData: [],
    productTrendData: [],
    productBrandData: [],
    productColorData: [],
    productOccasionData: [],
    productTypeData: []
  });
  const [sizeData, setSizeData] = useState<string[]>(['Free Size']);

  const areAtLeastTwoImagesSelected = (): boolean => {
    const imageValues = Object.values(selectedImage);
    const filledImageCount = imageValues.filter(image => image !== '').length;
    return filledImageCount >= 2;
  };

  const handleSizeChange = (event: any): void => {
    const { target: { value } } = event;
    
    if (sizeData[0] === 'Free Size') {
      setSizeData([value[1]]);
    } else if (value.includes('Free Size')) {
      setSizeData(['Free Size']);
    } else {
      setSizeData(typeof value === 'string' ? value.split(',') : value);
    }
  };

  const handleOnImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name } = e.target;
    const file = e.target.files?.[0];
    
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        const image = new Image();
        image.src = result;
        image.onload = () => {
          setSelectedImage({ ...selectedImage, [name]: file });
        };
      } else {
        console.error('Failed to read image file.');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleOnVideoChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSelectedVideo(e.target.files?.[0]);
  };

  const getCategoryData = async (): Promise<void> => {
    try {
      const res = await serverGetCategory();
      setCategoryData(res?.data || []);
    } catch (error) {
      console.error('Error fetching category data:', error);
    }
  };

  const getProductInformationData = async (): Promise<void> => {
    try {
      const [
        productPlatingData,
        productBaseMetalData,
        productStoneTypeData,
        productTrendData,
        productBrandData,
        productColorData,
        productOccasionData,
        productTypeData
      ] = await Promise.all([
        serverGetProductPlatingData(),
        serverGetProductBaseMetalData(),
        serverGetProductStoneTypeData(),
        serverGetProductTrendData(),
        serverGetProductBrandData(),
        serverGetProductColorData(),
        serverGetProductOccasionData(),
        serverGetProductTypeData()
      ]);

      setProductInformationData({
        productPlatingData: productPlatingData?.data || [],
        productBaseMetalData: productBaseMetalData?.data || [],
        productStoneTypeData: productStoneTypeData?.data || [],
        productTrendData: productTrendData?.data || [],
        productBrandData: productBrandData?.data || [],
        productColorData: productColorData?.data || [],
        productOccasionData: productOccasionData?.data || [],
        productTypeData: productTypeData?.data || []
      });
    } catch (error) {
      console.error('Error fetching product information data:', error);
    }
  };



  const schema = Yup.object().shape({
    categoryId: Yup.string().required("Please select a category.*"),
    name: Yup.string().required("Name is a required field.*"),
    code: Yup.string().required("HSN Code is a required field.*"),
    price: Yup.string().required("Price is a required field.*"),
    mrp: Yup.string().required("MRP is a required field.*"),
    qty: Yup.string().required("Quantity is a required field.*"),
    gst: Yup.string().required("GST(%) is a required field.*"),
    productBaseMetalId: Yup.string().required("Base Metal is a required field.*"),
    productPlatingId: Yup.string().required("Plating is a required field.*"),
    productStoneTypeId: Yup.string().required("Stone Type is a required field.*"),
    productTrendId: Yup.string().required("Trend is a required field.*"),
    inSuratCityCharge: Yup.string().required("Surat city charge is a required field.*"),
    inGujratStateCharge: Yup.string().required("Gujrat state charge is a required field.*"),
    inOutStateCharge: Yup.string().required("Out state charge is a required field.*"),
  });

  const appendProductFieldsToFormData = (formData: FormData, data: any): void => {
    // Required fields
    formData.append('categoryId', data.categoryId);
    formData.append('name', data.name);
    formData.append('code', data.code);
    formData.append('price', data.price.toString());
    formData.append('mrp', data.mrp.toString());
    formData.append('qty', data.qty.toString());
    formData.append('gst', data.gst);
    formData.append('inSuratCityCharge', data.inSuratCityCharge.toString());
    formData.append('inGujratStateCharge', data.inGujratStateCharge.toString());
    formData.append('inOutStateCharge', data.inOutStateCharge.toString());
    formData.append('isPause', data.isPause?.toString() || 'false');
    formData.append('isPramotion', data.isPramotion?.toString() || 'false');

    // Optional fields
    const optionalFields = [
      'productBaseMetalId', 'productPlatingId', 'productStoneTypeId', 'productTrendId',
      'productBrandId', 'productColorId', 'productOccasionId', 'productTypeId',
      'weight', 'description', 'discount', 'reward'
    ];

    optionalFields.forEach(field => {
      if (data[field]) {
        formData.append(field, data[field].toString());
      }
    });

    // Handle size array
    if (data.size && Array.isArray(data.size)) {
      data.size.forEach((s: string) => formData.append('size', s));
    }
  };

  const handleSubmit = async (data: any): Promise<void> => {
    try {
      setLoading(true);

      // Validate minimum 2 images
      const imageFiles = data?.imagesData?.filter(
        (img: any) => img instanceof File || typeof img === 'string'
      );
      
      if (!imageFiles || imageFiles.length < 2) {
        alert('Please select minimum two images.');
        setLoading(false);
        return;
      }

      const formData = new FormData();

      if (editDataObject) {
        // Edit mode: Handle existing vs new images
        const newImageFiles: File[] = [];
        const existingImageUrls: Array<{ _id?: string; path: string }> = [];
        
        data.imagesData.forEach((image: File | string) => {
          if (image instanceof File) {
            newImageFiles.push(image);
          } else if (typeof image === 'string') {
            const existingImage = editDataObject.image.find(img => img.path === image);
            if (existingImage) {
              existingImageUrls.push({
                _id: existingImage._id?.toString(),
                path: existingImage.path
              });
            }
          }
        });

        // Calculate deleted images
        const deleteImages = editDataObject.image
          .filter(img => !data.imagesData.includes(img.path))
          .map(img => ({
            _id: img._id?.toString(),
            path: img.path
          }));

        // Append images
        newImageFiles.forEach(file => formData.append('image', file));
        if (existingImageUrls.length > 0) {
          formData.append('existingImages', JSON.stringify(existingImageUrls));
        }
        if (deleteImages.length > 0) {
          formData.append('deleteImages', JSON.stringify(deleteImages));
        }

        // Handle video for edit mode
        if (data?.videoData) {
          if (data.videoData instanceof File) {
            formData.append('video', data.videoData);
            if (editDataObject.videoPath) {
              formData.append('deleteVideo', editDataObject.videoPath);
            }
          } else if (typeof data.videoData === 'string') {
            formData.append('existingVideo', data.videoData);
          }
        } else if (editDataObject.videoPath) {
          formData.append('deleteVideo', editDataObject.videoPath);
        }

        formData.append('_id', editDataObject._id.toString());
      } else {
        // Add mode: Append all images as new files
        data.imagesData.forEach((image: File) => {
          if (image instanceof File) {
            formData.append('image', image);
          }
        });

        if (data?.videoData && data.videoData instanceof File) {
          formData.append('video', data.videoData);
        }
      }

      // Append all product fields
      appendProductFieldsToFormData(formData, data);

      // Submit to server
      await (editDataObject ? serverUpdateProduct(formData) : serverInsertProduct(formData));

      handleClose();
      getProductData();
    } catch (error) {
      console.error('Error submitting product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDivision = (data: number): void => {
    setDivision(data);
  };

  // Initialize data on mount
  useEffect(() => {
    getCategoryData();
    getProductInformationData();
  }, []);

  // Reset form when dialog opens/closes
  useEffect(() => {
    setDivision(ProductDivision.Division1);
    setSelectedImage({
      image1: editDataObject?.image[0]?.path || '',
      image2: editDataObject?.image[1]?.path || '',
      image3: editDataObject?.image[2]?.path || '',
      image4: editDataObject?.image[3]?.path || ''
    });
    setSelectedVideo(editDataObject?.videoPath || undefined);
  }, [open, editDataObject]);

  // Update size data when edit object changes
  useEffect(() => {
    if (editDataObject) {
      setSizeData(editDataObject.size || ['Free Size']);
    }
  }, [editDataObject]);

  return {
    handleSubmit,
    loading,
    schema,
    categoryData,
    handleChangeDivision,
    division,
    handleOnImageChange,
    selectedImage,
    handleOnVideoChange,
    selectedVideo,
    setSelectedImage,
    setSelectedVideo,
    productInformationData,
    areAtLeastTwoImagesSelected,
    sizeData,
    handleSizeChange
  };
};

export default useAddProduct;
